import { forwardRef } from 'react';
import ContactCard from '../ContactCard';
import { ScaledBlock } from '../layout/ContentScale';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Star } from 'lucide-react';

function contactName(c) {
  return c.first_name || c.last_name || c.work?.company || c.organization || 'Unnamed';
}
function contactSub(c) {
  const role = [c.work?.title, c.work?.company || c.company].filter(Boolean).join(' · ');
  return role || c.addresses?.[0]?.city || (c.pikai_connection_status === 'connected' ? 'On PeopleiKnow' : '');
}

/**
 * The grouped-results renderer for SearchPage.
 *  • layout="grid" (default): 3-col avatar-tile grid — used on mobile/tablet.
 *  • layout="list": single-column selectable rows — used inside the desktop
 *    master–detail split (a click selects, it does not navigate).
 */
const SearchResultsGrid = forwardRef(function SearchResultsGrid({
  visibleKeys,
  contactsByGroup,
  letterRefs,
  sentinelRef,
  showSentinel,
  sortBy,
  layout = 'grid',
  selectedId = null,
  onSelect,
}, ref) {
  const isList = layout === 'list';
  return (
    <div className="flex-1 min-w-0" ref={ref}>
      {visibleKeys.map((key) => {
        const groupContacts = contactsByGroup[key];
        if (!groupContacts?.length) return null;
        return (
          <div
            key={key}
            ref={(el) => { letterRefs.current[key] = el; }}
            className={isList ? 'mb-2' : 'mb-6'}
          >
            <div className="pinch-fixed sticky top-0 z-10 bg-background py-2">
              <h3 className="text-lg font-bold text-nav-blue">{key}</h3>
            </div>
            <ScaledBlock>
            {isList ? (
              <div className="rounded-xl overflow-hidden border border-border bg-card" data-testid={`search-results-${key}`}>
                {groupContacts.map((c) => {
                  const initials = `${(c.first_name || '')[0] || ''}${(c.last_name || '')[0] || ''}`.toUpperCase();
                  const imageUrl = c.image_url?.startsWith('/api')
                    ? `${process.env.REACT_APP_BACKEND_URL}${c.image_url}`
                    : c.image_url;
                  const sel = selectedId === c.id;
                  return (
                    <button
                      key={c.id}
                      onClick={() => onSelect?.(c.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-border last:border-b-0 transition-colors ${
                        sel ? 'bg-primary/10' : 'hover:bg-muted'
                      }`}
                      style={sel ? { boxShadow: 'inset 3px 0 0 hsl(var(--primary))' } : undefined}
                      data-testid={`contact-row-${c.id}`}
                    >
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarImage src={imageUrl} alt={contactName(c)} className="object-cover" loading="lazy" />
                        <AvatarFallback className="bg-nav-blue text-white text-xs font-bold">{initials}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground leading-tight line-clamp-2">
                          {contactName(c)} {c.last_name && sortBy !== 'last_name' ? c.last_name : ''}
                        </p>
                        {contactSub(c) && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{contactSub(c)}</p>
                        )}
                      </div>
                      {c.is_favorite && <Star size={13} className="text-signal-yellow shrink-0" fill="currentColor" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4" data-testid={`search-results-${key}`}>
                {groupContacts.map((c) => (
                  <ContactCard key={c.id} contact={c} size="small" sortBy={sortBy} />
                ))}
              </div>
            )}
            </ScaledBlock>
          </div>
        );
      })}
      {showSentinel && (
        <div ref={sentinelRef} className="py-4 text-center">
          <div className="w-6 h-6 border-2 border-nav-blue border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      )}
    </div>
  );
});

export default SearchResultsGrid;
